<script setup>
import { nextTick, ref } from 'vue';
import ChatIcon from './ChatIcon.vue';

defineProps({
  placeholder: { type: String, required: true },
  t: { type: Function, required: true },
});

const emit = defineEmits(['send', 'number']);
const text = ref('');
const field = ref(null);

const resize = () => {
  const el = field.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 128)}px`;
};

const submit = async () => {
  const content = text.value.trim();
  if (!content) return;
  emit('send', content);
  text.value = '';
  await nextTick();
  resize();
};

const onKeydown = event => {
  if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
    event.preventDefault();
    submit();
  } else if (!text.value && /^[1-9]$/.test(event.key)) {
    // Digit on an empty composer picks that option from the tray, like a numbered WhatsApp menu.
    event.preventDefault();
    emit('number', Number(event.key));
  }
};

defineExpose({ focus: () => field.value?.focus({ preventScroll: true }) });
</script>

<template>
  <form
    class="flex shrink-0 items-end gap-2 bg-surface px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3"
    @submit.prevent="submit"
  >
    <textarea
      ref="field"
      v-model="text"
      rows="1"
      :placeholder="placeholder"
      class="max-h-32 min-h-[44px] flex-1 resize-none rounded-ark border border-line bg-canvas px-4 py-[11px] text-[16px] leading-[1.35] text-ink outline-none transition placeholder:text-muted focus:border-primary/50 focus:ring-4 focus:ring-primary/10 sm:text-[14.5px]"
      @input="resize"
      @keydown="onKeydown"
    />
    <button
      type="submit"
      class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-on-primary outline-none transition hover:brightness-110 focus-visible:ring-4 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-35"
      :disabled="!text.trim()"
      :aria-label="t('SEND')"
    >
      <ChatIcon name="send" class="h-5 w-5 translate-x-px" />
    </button>
  </form>
</template>
